import {
  Body,
  Controller,
  DatePicker,
  FormProvider,
  Input,
  MaterialIcon,
  Stack,
  Tooltip,
  useForm,
} from '@qred/components-library';
import { getCurrency } from '@qred/shared-component-library/src/currency';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { FieldPath } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import { AddressSearch } from '~/components/shared/AddressSearch/AddressSearch';
import { CardPurposeSelect } from '~/components/shared/CardPurposeSelect/CardPurposeSelect';
import {
  NLAddressLookup,
  NLAddressLookupSchema,
} from '~/components/shared/NLAddressLookup/NLAddressLookup';
import { TurnoverSelect } from '~/components/shared/TurnoverSelect/TurnoverSelect';
import {
  getPersonalNumberPlaceholder,
  marketHasAddress,
  marketHasDateOfBirth,
  marketHasPersonalNumber,
  marketShouldAskLoanApplicantForFirstAndLastName,
  marketShouldShowCreditCheckCopyTooltip,
  personalNumberIsOnlyNumbers,
} from '~/constants/markets';
import { CountryCode } from '~/enums';
import {
  normalizePersonalNumberForBackend,
  removeWhitespace,
} from '~/helpers/formatters.helper';
import { buildStreetValue } from '~/helpers/shared.helper';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { PostcodeAddress } from '~/hooks/usePostcode';
import useTranslate from '~/hooks/useTranslate';
import { ICardApplicationApplicant } from '~/interfaces/cardApplication';
import { RootState } from '~/store';
import { trackCardApplicationActions } from '~/store/actions/analyticsActions';
import {
  updateApplicantData,
  updateForm,
} from '~/store/slices/cardApplication.slice';
import {
  AdditionalInformationForm,
  createAdditionalInformationSchema,
  createPersonalNumberSchema,
  dateOfBirthSchema,
  DEAddressSchema,
  firstAndLastNameSchema,
} from './AdditionalInformation.schema';

const DEFAULT_DATE_OF_BIRTH = new Date(
  new Date().setFullYear(new Date().getFullYear() - 18)
);

export const AdditionalInformation = () => {
  const t = useTranslate();
  const {
    intl: { market, language },
    cardApplication: {
      form: {
        turnover: formTurnover,
        purposeOfCard,
        cardPurposeManual,
        applicant: {
          firstName,
          lastName,
          dateOfBirth,
          address,
          addressFields,
          personalNumber,
        },
      },
    },
  } = useSelector((state: RootState) => state);

  const initialDEAddressSearchValue = address
    ? [address.street, address.zipCode, address.city].join(', ')
    : undefined;
  const [DEAddressFieldValue, setDEAddressFieldValue] = useState(
    initialDEAddressSearchValue
  );

  const getInitialAddress = () => {
    return market === CountryCode.NL ? addressFields : address;
  };

  const formMethods = useForm<AdditionalInformationForm>({
    schema: createAdditionalInformationSchema(market),
    defaultValues: {
      firstName: marketShouldAskLoanApplicantForFirstAndLastName[market]
        ? firstName
        : undefined,
      lastName: marketShouldAskLoanApplicantForFirstAndLastName[market]
        ? lastName
        : undefined,
      dateOfBirth:
        marketHasDateOfBirth[market] && dateOfBirth
          ? new Date(dateOfBirth)
          : undefined,
      personalNumber: marketHasPersonalNumber[market]
        ? personalNumber || ''
        : undefined,
      address: marketHasAddress[market] ? getInitialAddress() : undefined,
      cardPurpose: {
        purposeOfCard,
        cardPurposeManual,
      },
      turnover: formTurnover,
    },
    mode: 'onTouched',
  });

  const {
    setValue,
    watch,
    register,
    control,
    formState: { errors, isValid, isValidating },
  } = formMethods;

  const fieldValues = watch();

  const dispatch = useAppDispatch();
  const validationContext = useContext(ValidationContext);

  const handleSelectedAddress = (selectedAddress: PostcodeAddress | null) => {
    if (selectedAddress) {
      const {
        locality,
        postcode,
        street,
        buildingNumber,
        buildingNumberAddition,
      } = selectedAddress.address;

      const fullStreet = `${street} ${buildingNumber || ''}${
        buildingNumberAddition || ''
      }`.trim();

      setValue(
        'address',
        {
          city: locality,
          street: fullStreet,
          zipCode: postcode,
        },
        { shouldValidate: true }
      );
    }
  };

  const onAddressSearchChange = (value: string) => {
    setValue('address', undefined, { shouldValidate: true });
    setDEAddressFieldValue(value);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();

    setValue(name as FieldPath<AdditionalInformationForm>, trimmedValue, {
      shouldValidate: true,
    });
    dispatch(
      trackCardApplicationActions({
        actionName: `${name}_change`,
      })
    );
  };

  useEffect(() => {
    if (isValid && !isValidating) {
      const updatedApplicant: Partial<ICardApplicationApplicant> = {};

      if (marketShouldAskLoanApplicantForFirstAndLastName[market]) {
        const firstAndLastNameForm = firstAndLastNameSchema.parse(fieldValues);
        updatedApplicant.firstName = firstAndLastNameForm.firstName;
        updatedApplicant.lastName = firstAndLastNameForm.lastName;
      }

      if (marketHasDateOfBirth[market]) {
        const dateOfBirthValue = dateOfBirthSchema.parse(
          fieldValues.dateOfBirth
        );
        updatedApplicant.dateOfBirth = dayjs(dateOfBirthValue).format(
          'YYYY-MM-DD'
        );
      }

      if (marketHasPersonalNumber[market]) {
        const personalNumberValue = createPersonalNumberSchema(market).parse(
          fieldValues.personalNumber
        );
        updatedApplicant.personalNumber = personalNumberValue;
      }

      if (marketHasAddress[market]) {
        if (market === CountryCode.DE) {
          const DEFieldValues = DEAddressSchema.parse(fieldValues.address);
          updatedApplicant.address = {
            city: DEFieldValues.city,
            street: DEFieldValues.street,
            zipCode: DEFieldValues.zipCode,
          };
        }

        if (market === CountryCode.NL) {
          const NLFieldValues = NLAddressLookupSchema.parse(fieldValues);
          updatedApplicant.addressFields = {
            zipCode: NLFieldValues.address.zipCode,
            houseNumber: NLFieldValues.address.houseNumber,
            houseNumberAddition: NLFieldValues.address.houseNumberAddition,
            city: NLFieldValues.address.city,
            streetName: NLFieldValues.address.streetName,
          };
          updatedApplicant.address = {
            city: NLFieldValues.address.city,
            street: buildStreetValue({ ...NLFieldValues.address }),
            zipCode: removeWhitespace(NLFieldValues.address.zipCode),
          };
        }
      }

      dispatch(updateApplicantData(updatedApplicant));
      dispatch(
        updateForm({
          purposeOfCard: fieldValues.cardPurpose.purposeOfCard,
          cardPurposeManual: fieldValues.cardPurpose.cardPurposeManual,
          turnover: fieldValues.turnover,
        })
      );
    } else {
      validationContext.addPropertyToValidationErrors(
        'additionalInformationStep'
      );
    }

    return () => {
      validationContext.removePropertyFromValidationErrors(
        'additionalInformationStep'
      );
    };
  }, [
    isValid,
    isValidating,
    market,
    fieldValues.firstName,
    fieldValues.lastName,
    fieldValues.dateOfBirth,
    fieldValues.personalNumber,
    fieldValues.address,
    fieldValues.cardPurpose.purposeOfCard,
    fieldValues.cardPurpose.cardPurposeManual,
    fieldValues.turnover,
    dispatch,
  ]);

  return (
    <FormProvider {...formMethods}>
      <Stack spacing={24}>
        <Input.Group orientation="vertical">
          {marketShouldAskLoanApplicantForFirstAndLastName[market] && (
            <>
              <Input
                {...register('firstName', { onBlur })}
                label={t('FirstName')}
                error={
                  errors?.firstName?.message && t(errors?.firstName?.message)
                }
                id="firstName"
              />
              <Input
                {...register('lastName', { onBlur })}
                label={t('LastName')}
                error={
                  errors?.lastName?.message && t(errors?.lastName?.message)
                }
                id="lastName"
              />
            </>
          )}

          {marketHasDateOfBirth[market] && (
            <Controller
              name="dateOfBirth"
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  label={t('DateOfBirth')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => {
                    field.onBlur();
                    dispatch(
                      trackCardApplicationActions({
                        actionName: 'dateOfBirth_change',
                      })
                    );
                  }}
                  error={
                    fieldState.error?.message && t(fieldState.error?.message)
                  }
                  valueFormat="DD-MM-YYYY"
                  defaultDate={DEFAULT_DATE_OF_BIRTH}
                  maxDate={new Date()}
                  id="dateOfBirth"
                />
              )}
            />
          )}

          {marketHasPersonalNumber[market] && (
            <Input
              {...register('personalNumber', {
                setValueAs: (value) =>
                  normalizePersonalNumberForBackend(value || '', market),
                onBlur,
              })}
              placeholder={getPersonalNumberPlaceholder(market, language)}
              label={t('PersonalNumber')}
              error={
                errors?.personalNumber?.message &&
                t(errors?.personalNumber?.message)
              }
              inputMode={
                personalNumberIsOnlyNumbers(market) ? 'numeric' : 'text'
              }
              rightSection={
                marketShouldShowCreditCheckCopyTooltip[market] ? (
                  <Tooltip
                    label={t('Onboarding.TooltipCreditCheckReportGenerated', {
                      TooltipCreditCheckImpactWorthiness: (
                        <Body size="md" weight={600}>
                          {t('Onboarding.TooltipCreditCheckImpactWorthiness')}
                        </Body>
                      ),
                    })}
                  >
                    <MaterialIcon src="help" size={'24'} />
                  </Tooltip>
                ) : undefined
              }
              id="personalNumber"
            />
          )}

          {marketHasAddress[market] && (
            <>
              {market === CountryCode.DE && (
                <Controller
                  name="address"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AddressSearch
                      label={t('CardApplication.ApplicantAddress')}
                      handleResult={handleSelectedAddress}
                      onChange={onAddressSearchChange}
                      onBlur={() => {
                        field.onBlur();
                        dispatch(
                          trackCardApplicationActions({
                            actionName: 'address_change',
                          })
                        );
                      }}
                      error={
                        fieldState.error?.message &&
                        t(fieldState.error?.message)
                      }
                      initialValue={DEAddressFieldValue}
                    />
                  )}
                />
              )}

              {market === CountryCode.NL && <NLAddressLookup />}
            </>
          )}
        </Input.Group>
        <Input.Group orientation="vertical">
          <CardPurposeSelect />
          <TurnoverSelect
            label={`${t('CardApplication.TurnoverLabel') as string} (${
              getCurrency(market).unit
            })`}
          />
        </Input.Group>
      </Stack>
    </FormProvider>
  );
};
